/**
 * Authentication Service for Client Portal
 *
 * Uses Firebase Authentication with Email/Password
 * Client credentials are managed via Firebase Console or Dashboard
 */
import { auth, db } from './firebase';
import {
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    type User as FirebaseUser
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { logLogin, logLogout } from './analytics';

export interface ClientUser {
    uid: string;
    email: string;
    clientId: string;
    clientName: string;
    role: 'client_partner';
    createdAt?: Date;
    lastLoginAt?: Date;
}

/**
 * Sign in client with email and password
 * After Firebase auth, fetch client profile from Firestore
 */
export async function signInClient(email: string, password: string): Promise<ClientUser> {
    try {
        // Authenticate with Firebase
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const firebaseUser = userCredential.user;

        // Fetch client profile from Firestore
        const clientProfile = await getClientProfile(firebaseUser.uid);

        if (!clientProfile) {
            // If no profile exists, this is first login - create profile
            // Client must exist in clients table (created via Dashboard)
            throw new Error('Profil client tidak ditemukan. Hubungi admin untuk aktivasi akun.');
        }

        // Update last login
        await updateLastLogin(firebaseUser.uid);

        // Log activity
        logLogin(clientProfile.clientId, email);

        return clientProfile;
    } catch (error: unknown) {
        const firebaseError = error as { code?: string; message?: string };
        console.error('Sign in error:', firebaseError);

        // Translate Firebase errors to user-friendly messages
        if (firebaseError.code === 'auth/user-not-found' || firebaseError.code === 'auth/wrong-password') {
            throw new Error('Email atau password salah');
        } else if (firebaseError.code === 'auth/invalid-email') {
            throw new Error('Format email tidak valid');
        } else if (firebaseError.code === 'auth/too-many-requests') {
            throw new Error('Terlalu banyak percobaan. Coba lagi nanti.');
        } else if (firebaseError.code === 'auth/invalid-credential') {
            throw new Error('Email atau password salah');
        }

        throw error;
    }
}

/**
 * Sign out client
 */
export async function signOutClient(): Promise<void> {
    const currentUser = auth.currentUser;
    if (currentUser) {
        const profile = await getClientProfile(currentUser.uid);
        if (profile) {
            logLogout(profile.clientId);
        }
    }
    await signOut(auth);
}

/**
 * Get client profile from Firestore
 */
export async function getClientProfile(uid: string): Promise<ClientUser | null> {
    try {
        const docRef = doc(db, 'client_users', uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            return {
                uid,
                email: data.email,
                clientId: data.clientId,
                clientName: data.clientName,
                role: 'client_partner',
                createdAt: data.createdAt?.toDate(),
                lastLoginAt: data.lastLoginAt?.toDate()
            };
        }
        return null;
    } catch (error) {
        console.error('Error fetching client profile:', error);
        return null;
    }
}

/**
 * Update last login timestamp
 */
async function updateLastLogin(uid: string): Promise<void> {
    try {
        const docRef = doc(db, 'client_users', uid);
        await setDoc(docRef, { lastLoginAt: serverTimestamp() }, { merge: true });
    } catch (error) {
        console.warn('Failed to update last login:', error);
    }
}

/**
 * Subscribe to auth state changes
 */
export function onAuthChange(callback: (user: FirebaseUser | null) => void): () => void {
    return onAuthStateChanged(auth, callback);
}

/**
 * Get current Firebase user
 */
export function getCurrentUser(): FirebaseUser | null {
    return auth.currentUser;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
    return auth.currentUser !== null;
}
